// Named export: the default export of src/store is the store FACTORY.
import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import FileRef from "./FileRef";
import { DOCUMENT_TYPES } from "./DocumentRef";
import { deepFreeze, timestampToDate } from "../util/misc";

// The document types offered when uploading straight to a project or sale.
// Everything else in DOCUMENT_TYPES is produced by its own workflow (acks,
// bills, invoices, delivery reports…) and shows up in the Files tab from
// there, not from here.
export const PROJECT_FILE_TYPES = [
  "attachment",
  "project-media",
  "plans-specs",
  "shop-drawings",
  "contract",
  "signed-order",
  "purchase-order",
  "misc-legal",
];
deepFreeze(PROJECT_FILE_TYPES);

export const PROJECT_FILE_FIELDS = {
  // The sale the file belongs to (its letter, e.g. "A"), or null for a file
  // that belongs to the project as a whole.
  saleID: null,
  type: "attachment",
  // The type's DOCUMENT_TYPES `info` fields (e.g. a contract's total)
  info: {},
  file: null,
  storagePath: "",

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(PROJECT_FILE_FIELDS);

// A file uploaded straight to a project or one of its sales, from the Files
// tab. Stored under the project (projects/{projectID}/files) rather than as
// an array on the sale, so a locked or completed sale still takes files and
// two people uploading at once never overwrite each other's list.
export default class ProjectFile extends DataStructure {
  constructor(projectID, id, data = {}) {
    data = sanitize(data, PROJECT_FILE_FIELDS);

    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);
    if (!(data.type in DOCUMENT_TYPES)) {
      data.type = "attachment";
    }
    if (data.file) {
      data.file = new FileRef(data.file).data;
    }

    super(data);

    this.projectID = projectID || null;
    this.id = id || null;
    this.permissions = Object.freeze({
      create: ProjectFile.canBeCreatedBy,
      edit: this.canBeEditedBy,
      delete: this.canBeDeletedBy,
    });
  }

  // Permissions

  static canBeCreatedBy(user) {
    return Boolean(user && user.isEmployee);
  }

  // The uploader can fix their own mistakes; anyone else's file is an
  // administrator's call, the same line the firestore rules draw.
  canBeEditedBy(user) {
    return Boolean(
      user &&
        user.isEmployee &&
        (user.isAdmin || user.id === this.data.createdBy)
    );
  }

  canBeDeletedBy(user) {
    return this.canBeEditedBy(user);
  }

  // Validation

  get isValid() {
    return Boolean(this.data.file && this.data.file.url);
  }

  // Relationships

  get createdByUser() {
    return store.state.main.users[this.data.createdBy] || null;
  }

  // Formatting

  get file() {
    return this.data.file ? new FileRef(this.data.file) : null;
  }

  get documentType() {
    return DOCUMENT_TYPES[this.data.type];
  }

  toString() {
    return this.data.file ? this.data.file.name : "";
  }

  // Database

  get path() {
    return ProjectFile.getPath(this.projectID, this.id);
  }

  static getPath(projectID, id) {
    let path = `projects/${projectID}/files`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  // Storage path of an upload. The document ID prefix keeps two files with
  // the same name apart; the original name survives for friendlier
  // downloads (the Document Library's `<docID>__<name>` shape).
  static getStoragePath(projectID, id, name) {
    const safeName = String(name || "file").replace(/[^A-Za-z0-9._-]+/g, "_");
    return `projects/${projectID}/files/${id}__${safeName}`;
  }

  // An image's display copy (at most 2048px), written beside it by the
  // projectFileDisplayCopy function -- keep the path in step with
  // functions/projectFiles.js.
  static getDisplayStoragePath(projectID, id) {
    return `projects/${projectID}/files/display/${id}`;
  }

  // The display copy carries the original's download token, so its URL is
  // the original's with the object path swapped. Null if `url` isn't a
  // download URL for `storagePath`.
  static getDisplayURL(projectID, id, url, storagePath) {
    const objectPath = `/o/${encodeURIComponent(storagePath || "")}?`;
    if (!storagePath || !String(url || "").includes(objectPath)) return null;
    return url.replace(
      objectPath,
      `/o/${encodeURIComponent(
        ProjectFile.getDisplayStoragePath(projectID, id)
      )}?`
    );
  }

  get displayURL() {
    return this.data.file
      ? ProjectFile.getDisplayURL(
          this.projectID,
          this.id,
          this.data.file.url,
          this.data.storagePath
        )
      : null;
  }

  static toFirestore(projectFile) {
    return projectFile.data;
  }

  static fromFirestore(snapshot, options) {
    return new ProjectFile(
      snapshot.ref.parent.parent.id,
      snapshot.id,
      snapshot.data(options)
    );
  }
}
